import moment from 'moment';
import { ContractNotificationEmailHTML } from 'notification_templates/ContractCreatedEmail';
import { useEffect, useState } from 'react';
import customAxios from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';
import { useSendContractNotification } from './useSendContractNotification';
import { toLocalISO } from 'routes/Pages/GenTask/components/SharedFormFields';
import { useToast } from 'context/tostifyContext';

function useConvertToContract(contractRowData, siteId) {
  // ── Core contract identity ────────────────────────────────────────────────
  const [selectedContractType, setSelectedContractType] = useState('');
  const [selectedpurchaseType, setSelectedpurchaseType] = useState('');
  const [fellingLicenceNo, setFellingLicenceNo] = useState('');
  const [lableOfContract, setLableOfContract] = useState('');

  // ── Sustainability & certification ────────────────────────────────────────
  const [isProductSustainable, setIsProductSustainable] = useState(false);
  const [forestSourcingArea, setForestSourcingArea] = useState('');
  const [supplierType, setSupplierType] = useState('');
  const [validSelfDeclaration, setValidSelfDeclaration] = useState('');
  const [biomassSite, setBiomassSite] = useState('');
  const [forestGrower, setForestGrower] = useState('');
  const [contractWith, setContractWith] = useState('');
  const [harvestedBy, setHarvestedBy] = useState('WTG');
  const [tripartiteAgreement, setTripartiteAgreement] = useState('');
  const [certificationNote, setCertificationNote] = useState('');

  const [openConvertToContract, setOpenConvertToContract] = useState(false);

  // ── Rates ─────────────────────────────────────────────────────────────────
  const [agreedRates, setAgreedRates] = useState({
    // H&F (Harvesting & Forwarding) — shown in modal + email
    harvestingRate: '',
    harvestingUOM: '', // e.g. "Per Tonne"
    forwardingRate: '',
    forwardingUOM: '', // e.g. "Per Tonne"
    harvestingConfirmedBy: '',
    // Coillte-specific
    coillteRoadsideRate: '',
    shortStakesPremium: '',
    coillteRateConfirmedBy: '',
  });

  const [productPricing, setProductPricing] = useState([]);

  // ── VAT ───────────────────────────────────────────────────────────────────
  const [vatRate, setVatRates] = useState('5');
  const [otherVatRate, setOtherVatRate] = useState(false);

  // ── Notes & timeline ──────────────────────────────────────────────────────
  const [internalNotes, setInternalNotes] = useState('');
  const [growerNotes, setGrowerNotes] = useState('');
  const [contractStartDate, setContractStartDate] = useState(toLocalISO);
  const [contractEndDate, setContractEndDate] = useState(toLocalISO);

  // ── Files ─────────────────────────────────────────────────────────────────
  const [uploadedFiles, setUploadedFiles] = useState([]);
  // Signed grower contract — mandatory, tracked separately so validation is unambiguous
  const [signedContractFile, setSignedContractFile] = useState(null);

  // ── Notification contact — mandatory, at least email or phone ─────────────
  const [notificationContact, setNotificationContact] = useState({ email: '', phone: '' });

  // ── Account details ───────────────────────────────────────────────────────
  const [accountDetails, setAccountDetails] = useState({
    accountHolder: '',
    bankName: '',
    iban: '',
    bic: '',
  });

  // ── Site info (fetched) ───────────────────────────────────────────────────
  const [siteInfoData, setSiteInfoData] = useState({
    siteAssessmentType: '',
    siteAssessmentCategory: '',
    landownerName: '',
    address: '',
    siteLocation: '',
    totalArea: '',
    whNumber: '',
    fellingLicenceNo: '',
    date: '',
    crmID: '',
  });

  const [assessmentInfo, setAssessmentInfo] = useState({});

  // ── Questionnaire ─────────────────────────────────────────────────────────
  const [questionnaire, setQuestionnaire] = useState({
    firstThinning: false,
    secondThinning: false,
    clearFell: false,
    treesLeft: '',
    agreedWithGrower: '',
    differentQuantitiesNote: '',
    requireHardCopy: '',
    archaeologicalArea: false,
    recreationalArea: false,
    waterways: false,
    powerPhoneLines: false,
    railLines: false,
    nearbyDwellings: false,
    other: false,
    otherDetails: '',
    permitDocRequired: 'No',
    supplierDocRequired: 'No',
  });

  const { sendNotification } = useSendContractNotification();
  const { showToast } = useToast();

  // ── Data fetching ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!siteId) return;
    fetchSiteDetails();
    fetchAssessmentData();
  }, [siteId]);

  const fetchSiteDetails = async () => {
    try {
      const res = await customAxios.get(`/CRM/GetCRMSiteDetails?siteAssessmentId=${siteId}`, {
        headers: authHeader(),
      });
      const crmData = res?.data?.[0];
      if (!crmData) return;

      setLableOfContract(crmData.siteDetailsRef);
      if (crmData.licenseNo) setFellingLicenceNo(crmData.licenseNo);

      setSiteInfoData({
        siteAssessmentType: crmData.siteAssessmentType || 'N/A',
        siteAssessmentCategory: crmData.siteAssessmentCategory || 'N/A',
        landownerName: crmData.ownerName || 'N/A',
        address: crmData.address1 || 'N/A',
        siteLocation: crmData.siteDetailsRef || 'N/A',
        totalArea: crmData.siteVolAreaInHa || 'N/A',
        whNumber: crmData.wH_Number || 'N/A',
        fellingLicenceNo: crmData.licenseNo || 'N/A',
        date: new Date(crmData.submissionDate).toLocaleDateString(),
        crmID: crmData.crmID,
      });

      // Fetch product pricing with full rate breakdown
      const pricingRes = await customAxios.get(`/Ops/GetTimberPricing?siteAssessmentID=${siteId}`, {
        headers: authHeader(),
      });
      setProductPricing(
        pricingRes.data.map(item => ({
          product: item.product,
          destination: item.destination,
          haulageRate: item.haulage,
          harvestingRate: item.harvestingCostsPerTonne,
          extractionRate: item.extractionCosts,
          purchasePrice: item.purchasesPrice,
          tonnes: item.tonnes,
        })),
      );

      // Pre-fill sourcing area and grower name from enquiry
      const enquiryRes = await customAxios.get(`/Enquiry/GetEnquiryDetails?crmID=${crmData.crmID}`, {
        headers: authHeader(),
      });
      const enquiry = enquiryRes.data?.[0];
      if (enquiry) {
        setForestSourcingArea(enquiry.forestCounty || '');
        setForestGrower(enquiry.enquirerName || '');
        // Pre-fill notification email if available on the enquiry record
        if (enquiry.email) {
          setNotificationContact(prev => ({ ...prev, email: enquiry.email }));
        }
        if (enquiry.phone) {
          setNotificationContact(prev => ({ ...prev, phone: enquiry.phone }));
        }
      }
    } catch (err) {
      console.error('fetchSiteDetails error:', err);
    }
  };

  const fetchAssessmentData = async () => {
    try {
      const res = await customAxios.get(`/SiteDetails/GetSiteAssessmentInfo/${siteId}`, {
        headers: authHeader(),
      });
      if (res.data?.length > 0) {
        const { standingType } = res.data[0];
        if (String(standingType).startsWith('1st')) {
          handleQuestionnaireChange('firstThinning', true);
        } else if (String(standingType).startsWith('2nd')) {
          handleQuestionnaireChange('secondThinning', true);
        } else if (String(standingType).startsWith('Clear')) {
          handleQuestionnaireChange('clearFell', true);
        }
      }
      setAssessmentInfo(res.data?.[0] ?? {});
    } catch (err) {
      console.error('fetchAssessmentData error:', err);
    }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  // Handles generic office documents/annexes

  const handleFileUpload = files => {
    const fileArray = Array.from(files);
    const formattedFiles = fileArray.map(file => ({
      file: file,
      docType: 'Office Document',
    }));
    setUploadedFiles(prev => [...prev, ...formattedFiles]);
  };

  const removeFile = indexToRemove => {
    setUploadedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  // Handles the specific Signed Contract file
  const handleSignedContractUpload = files => {
    const file = Array.from(files)[0];
    if (file) {
      setSignedContractFile({
        file: file,
        docType: 'Signed Contract',
      });
    }
  };

  const removeSignedContractFile = () => {
    setSignedContractFile(null);
  };
  const handleQuestionnaireChange = (field, value) => {
    setQuestionnaire(prev => ({ ...prev, [field]: value ?? 'No' }));
  };

  const handleAccountChange = (field, value) => {
    setAccountDetails(prev => ({ ...prev, [field]: value }));
  };

  const handleNotificationContactChange = (field, value) => {
    setNotificationContact(prev => ({ ...prev, [field]: value ?? '' }));
  };

  // ── Notification builder ──────────────────────────────────────────────────
  const buildContractNotificationData = (contractUniqueID, docLinks = [], resolvedContact) => {
    // Build the contract detail URL — update the path to match your actual route
    const contractUrl = `https://logwatch.myreports.online/siteContracts?contractUniqueID=${contractUniqueID}`;

    return {
      // Header / meta
      contractUniqueID,
      contractTitle: lableOfContract,
      createdAt: moment().format('lll'),
      siteAssessmentRef: contractRowData?.siteAssessmentID ?? siteInfoData.whNumber,
      fellingLicenceNo: siteInfoData.fellingLicenceNo,
      pricingVersion: contractRowData?.pricingVersion
        ? `${contractRowData.pricingVersion} ${contractRowData.timberPricingLabel ?? ''}`.trim()
        : '—',

      // Supplier / site summary
      supplierName: forestGrower,
      landownerName: siteInfoData.landownerName,
      address: siteInfoData.address,
      county: forestSourcingArea,
      totalArea: siteInfoData.totalArea,
      supplierType,
      harvestedBy,
      confirmedBy: agreedRates.coillteRateConfirmedBy || agreedRates.harvestingConfirmedBy,

      // Notification contact (grower contact captured in modal)
      notificationContact: {
        email: resolvedContact.email ?? '',
        phone: resolvedContact.phone ?? '',
      },

      // Contract details
      contractDetails: {
        contractType: selectedContractType || siteInfoData.siteAssessmentType,
        purchaseType: selectedpurchaseType || siteInfoData.siteAssessmentCategory,
        startDate: contractStartDate,
        finishDate: contractEndDate,
        notesToOperators: growerNotes,
        internalNotes,
        labelOfContract: lableOfContract,
        vatRate,
      },

      // Certification
      certificationInfo: isProductSustainable
        ? {
            county: forestSourcingArea,
            supplierType,
            validSelfDeclaration,
            biomassSite,
            forestGrower,
            contractWith,
            harvestedBy,
            tripartiteAgreement,
            notes: certificationNote,
            coillteRoadsideRate: agreedRates.coillteRoadsideRate,
            shortStakesPremium: agreedRates.shortStakesPremium,
            coillteRateConfirmedBy: agreedRates.coillteRateConfirmedBy,
          }
        : null,

      // Per-product rows with destination + haulage — for the rate table in the email
      productPricing: productPricing.map(item => ({
        product: item.product,
        destination: item.destination,
        haulageRate: item.haulageRate,
        harvestingRate: item.harvestingRate,
        extractionRate: item.extractionRate,
        purchasePrice:
          item.purchasePrice != null && !isNaN(item.purchasePrice) ? Number(item.purchasePrice).toFixed(2) : '—',
      })),

      // Account details
      accountDetails: {
        accountHolder: accountDetails.accountHolder,
        bankName: accountDetails.bankName,
        iban: accountDetails.iban,
        bic: accountDetails.bic,
      },

      // Uploaded docs list (for email footer)
      uploadedDocuments: docLinks,

      // Deep link — use the correct route for your app
      contractUrl,
    };
  };

  // ── Send notification ─────────────────────────────────────────────────────
  const sendContractNotification = async contractUniqueID => {
    let docLinks = [];
    try {
      const contractDocsResponse = await customAxios.get(`/Ops/GetContractsDocs/${contractUniqueID}`, {
        headers: authHeader(),
      });

      docLinks = (contractDocsResponse.data ?? []).map(doc => ({
        name: doc.documentName,
        docType: doc.docType,
        url: `https://logwatch-api.myreports.online/api/Ops/GetContractsFilePublic/${contractUniqueID}/${doc.id}`,
      }));
    } catch (docErr) {
      console.warn('Could not fetch contract docs for links:', docErr);
      // non-fatal — email still sends, just without doc links
    }

    // Pull the actual persisted notification contact (first email + first phone)
    let resolvedContact = { email: '', phone: '' };
    try {
      const notifyRes = await customAxios.get(`/Ops/GetContractsUsersToNotify?contractUniqueID=${contractUniqueID}`, {
        headers: authHeader(),
      });

      const notifyRecords = notifyRes.data ?? [];

      const firstEmail = notifyRecords.find(r => r.notificationType?.toLowerCase() === 'email' && r.emailOrPhone?.trim());
      const firstPhone = notifyRecords.find(r => r.notificationType?.toLowerCase() === 'sms' && r.emailOrPhone?.trim());

      resolvedContact = {
        email: firstEmail?.emailOrPhone ?? '',
        phone: firstPhone?.emailOrPhone ?? '',
      };
    } catch (notifyErr) {
      console.warn('Could not fetch contract notification contacts, falling back to local state:', notifyErr);
      resolvedContact = {
        email: notificationContact.email ?? '',
        phone: notificationContact.phone ?? '',
      };
    }

    try {
      const notificationData = buildContractNotificationData(contractUniqueID, docLinks, resolvedContact);
      const html = ContractNotificationEmailHTML(notificationData);

      // Internal recipients always notified
      const recipients = ['accounts@worrellharvesting.ie', '1b4c4a2d.worrellharvesting.ie@emea.teams.ms'];

      // Add grower email if provided
      if (resolvedContact.email?.trim()) {
        recipients.push(resolvedContact.email.trim());
      }

      await Promise.allSettled(
        recipients.map(email =>
          sendNotification({
            sendTo: email,
            subject: `New Contract Created — ${contractUniqueID} - ${lableOfContract}`,
            body: html,
            deliveryType: 'EMAIL',
            primaryKey: null,
            uniqueRef: contractUniqueID,
            templateType: null,
            rawData: JSON.stringify(notificationData),
            notificationSection: 'Contract',
          }),
        ),
      );

      // SMS to grower phone if provided
      if (resolvedContact.phone?.trim()) {
        await sendNotification({
          sendTo: resolvedContact.phone.trim(),
          subject: `Contract Created`,
          body: `Your contract ${lableOfContract ||
            contractUniqueID} has been created. Please check your email for details.`,
          deliveryType: 'SMS',
          primaryKey: null,
          uniqueRef: contractUniqueID,
          templateType: null,
          rawData: null,
        });
      }

      showToast('Notification sent successfully', 'success');
    } catch (err) {
      console.error('sendContractNotification error:', err);
      showToast('Failed to send notification', 'error');
    }
  };

  return {
    // Contract identity
    selectedContractType,
    setSelectedContractType,
    selectedpurchaseType,
    setSelectedpurchaseType,
    fellingLicenceNo,
    setFellingLicenceNo,
    lableOfContract,
    setLableOfContract,

    // Sustainability & certification
    isProductSustainable,
    setIsProductSustainable,
    forestSourcingArea,
    setForestSourcingArea,
    supplierType,
    setSupplierType,
    validSelfDeclaration,
    setValidSelfDeclaration,
    biomassSite,
    setBiomassSite,
    forestGrower,
    setForestGrower,
    contractWith,
    setContractWith,
    harvestedBy,
    setHarvestedBy,
    tripartiteAgreement,
    setTripartiteAgreement,
    certificationNote,
    setCertificationNote,

    // Rates
    agreedRates,
    setAgreedRates,
    productPricing,

    // VAT
    vatRate,
    setVatRates,
    otherVatRate,
    setOtherVatRate,

    // Notes & timeline
    internalNotes,
    setInternalNotes,
    growerNotes,
    setGrowerNotes,
    contractStartDate,
    setContractStartDate,
    contractEndDate,
    setContractEndDate,

    // Files
    uploadedFiles,
    handleFileUpload,
    removeFile,
    signedContractFile,
    handleSignedContractUpload,
    removeSignedContractFile,

    // Notification contact
    notificationContact,
    handleNotificationContactChange,

    // Account details
    accountDetails,
    handleAccountChange,

    // Site info & questionnaire
    siteInfoData,
    assessmentInfo,
    questionnaire,
    handleQuestionnaireChange,

    // Actions
    sendContractNotification,

    openConvertToContract,
    setOpenConvertToContract,
  };
}

export default useConvertToContract;
